/**
 * M1.1 session-control client skeleton (offline by default).
 *
 * The daemon may advertise `SessionSummary.control` (additive, default disabled).
 * This module keeps everything testable without React/WebSocket:
 * - capability/availability gating (demo/offline/non-DSH/disabled);
 * - 8 KiB UTF-8 text validation and command payload building;
 * - the canonical Ed25519 signing transcript from `@luminis/protocol`;
 * - the `session.command.result` status machine (dedupe/reconcile rules).
 *
 * No command is ever sent from here; AppState/controller only relay the sealed
 * payload. M1.1 does not implement ACP or real session writes.
 */
import {
  SESSION_COMMAND_RESULT_EVENT,
  SESSION_COMMAND_TEXT_MAX_BYTES,
  isSessionCommandMode,
  isSessionCommandResult,
  isSignedSessionCommandPayload,
  sessionCommandSigningPayload,
  type SessionCommandAction,
  type SessionCommandMode,
  type SessionCommandPayload,
  type SessionCommandResult,
  type SessionCommandStatus,
  type SessionControlCapability,
  type SessionControlInfo,
} from '@luminis/protocol';
import { signStandardBase64 } from './crypto/e2e';
import { base64UrlEncode, randomBytes } from './id';
import type { SessionView } from './types';

/**
 * v0.2.0 mobile UI scope: message + interrupt only (pause/resume deferred).
 *
 * Typed as control capabilities (not the broader `SessionCommandAction` set),
 * so the v0.5.0 monitoring-only `ignore`/`unignore` actions can never leak into
 * the control panel advertisement/view model.
 */
export const SESSION_CONTROL_UI_ACTIONS: readonly SessionControlCapability[] = [
  'message',
  'interrupt',
];
const SESSION_CONTROL_UI_ACTION_SET: ReadonlySet<string> = new Set(SESSION_CONTROL_UI_ACTIONS);

/**
 * Per-command message admission modes the PWA may offer (v0.5.0, task-6).
 *
 * The daemon only advertises `control.modes` for sessions where `mode` actually
 * has an effect (desktop bridge); other adapters (ACP) ignore it. When the
 * advertisement does not include BOTH modes the selector stays hidden and the
 * signed payload omits `mode`, so the daemon's configured default applies and
 * pre-mode senders keep their exact behaviour.
 */
export const SESSION_CONTROL_UI_MODES: readonly SessionCommandMode[] = ['steer', 'queue'];

/** Safe default and the mode shown when the selector becomes visible. */
export const SESSION_COMMAND_MODE_DEFAULT: SessionCommandMode = 'steer';

export const SESSION_COMMAND_MODE_LABELS: Record<SessionCommandMode, string> = {
  steer: '实时插话',
  queue: '排到下一 turn',
};

/** Fixed UX copy: the two modes are NOT interchangeable and have different risk. */
export const SESSION_COMMAND_MODE_DESCRIPTIONS: Record<SessionCommandMode, string> = {
  steer: '非抢占：不能停止正在运行的工具，只把消息插入当前 turn。',
  queue: '不改动当前 turn：消息排到下一个 turn 才生效。',
};

/**
 * Narrow local view of the additive `SessionControlInfo.modes` advertisement.
 *
 * The typed protocol field lands in parallel (task-7); the phone must compile
 * both before and after it, and never trusts the wire shape either way, so the
 * value is read through this structural carrier and normalized below.
 */
type SessionControlModesCarrier = { modes?: unknown };

/**
 * Normalize the advertised per-command message modes: disabled control
 * advertises nothing, unknown values are dropped, duplicates removed and the
 * result keeps only the fixed `steer`/`queue` set.
 */
export function sessionControlModes(control: SessionControlInfo | undefined): SessionCommandMode[] {
  if (!control || !control.enabled) return [];
  const raw = (control as SessionControlInfo & SessionControlModesCarrier).modes;
  if (!Array.isArray(raw)) return [];
  const modes: SessionCommandMode[] = [];
  for (const value of raw) {
    if (isSessionCommandMode(value) && !modes.includes(value)) modes.push(value);
  }
  return modes;
}

/**
 * True only when the daemon advertises BOTH modes (desktop bridge sessions).
 * Otherwise the message mode selector stays hidden and no `mode` is sent.
 */
export function sessionControlOffersModeChoice(control: SessionControlInfo | undefined): boolean {
  const modes = sessionControlModes(control);
  return modes.includes('steer') && modes.includes('queue');
}

/**
 * Mode to put in the signed payload for a message command.
 *
 * With the selector visible (bridge session) the explicit choice is signed —
 * `steer` by default. Without the advertisement the field is omitted so the
 * daemon's configured default applies (ACP/legacy behaviour unchanged).
 */
export function resolveSessionCommandMode(
  control: SessionControlInfo | undefined,
  requested: SessionCommandMode | undefined,
): SessionCommandMode | undefined {
  if (!sessionControlOffersModeChoice(control)) return undefined;
  return requested === undefined ? SESSION_COMMAND_MODE_DEFAULT : requested;
}

export const SESSION_CONTROL_RISK_WARNINGS: readonly string[] = [
  '目标 agent 可能自动执行工具，发送内容可能触发实际动作',
  '仅对可丢弃的会话使用控制；不要用于重要会话',
  '控制产生的副作用不可回滚（M1.1 不提供 terminate/delete）',
  '每条命令都会单独确认并用手机 Ed25519 密钥签名；不提供批量或永久允许',
];

export type SessionControlUnavailableReason =
  'demo' | 'unpaired' | 'offline' | 'non_dsh' | 'relay_not_ready' | 'default_off' | 'unauthorized';

export interface SessionControlAvailability {
  available: boolean;
  reason?: string;
  /** Stable UI category (never derived from raw server text). */
  reasonCode?: SessionControlUnavailableReason;
  /** Capabilities the mobile UI is allowed to offer (already filtered). */
  actions: SessionCommandAction[];
}

/** Fixed copy for “the PC allowlist does not authorize this session”. */
export const SESSION_CONTROL_UNAUTHORIZED_COPY = '此会话未授权控制（PC 侧 allowlist）';
/** Fixed copy when control was never enabled on the daemon. */
export const SESSION_CONTROL_DEFAULT_OFF_COPY = 'daemon 未启用会话控制（默认关闭）';

/**
 * Server reason strings are not a stable contract yet. Map the known
 * allowlist/capability denial shapes to the unauthorized state and never render
 * the raw string (it may contain paths/tokens).
 */
export function isAllowlistDenialReason(reason: unknown): boolean {
  if (typeof reason !== 'string') return false;
  return /allowlist|allow\s*list|no\s+capabilit|not\s+(?:in\s+)?(?:the\s+)?allow|unauthori[sz]ed|not\s+permitted|permission\s+denied|denied\s+by/i.test(
    reason,
  );
}

export interface SessionControlContext {
  demoMode: boolean;
  paired: boolean;
  online: boolean;
  /** Live relay plan enabled AND the controller connected. */
  relayReady: boolean;
}

/** Map the additive `SessionSummary.control` into the phone view model. */
export function sessionControlFromSummary(control: SessionControlInfo | undefined):
  | {
      enabled: boolean;
      capabilities: SessionControlCapability[];
      /** Advertised per-command message modes (empty/omitted when unsupported). */
      modes?: SessionCommandMode[];
      reason?: string;
    }
  | undefined {
  if (!control) return undefined;
  const capabilities = control.capabilities.filter(
    (capability): capability is SessionControlCapability =>
      SESSION_CONTROL_UI_ACTION_SET.has(capability),
  );
  const modes = sessionControlModes(control);
  const reason = control.reason?.trim();
  return {
    enabled: control.enabled,
    capabilities,
    ...(modes.length > 0 ? { modes } : {}),
    ...(reason ? { reason: reason.slice(0, 200) } : {}),
  };
}

/**
 * Default-off gating. The panel is visible but disabled unless *all* conditions
 * hold; the returned reason/code is the only thing shown to the user (never raw
 * server text, so paths/tokens cannot leak).
 *
 * Distinct categories (v0.4.0 direction C):
 * - `unauthorized`: control exists but the PC allowlist does not grant this
 *   session (`enabled=false` + allowlist/no-capability reason, or no supported
 *   capabilities). The panel shows the fixed allowlist copy and HIDES the action
 *   buttons — no command is attempted.
 * - `default_off`: no control advertisement / no reason at all (daemon never
 *   enabled control). Existing disabled-button UX stays.
 * - `demo`/`unpaired`/`offline`/`non_dsh`/`relay_not_ready`: unchanged.
 */
export function sessionControlAvailability(
  session: Pick<SessionView, 'source' | 'status'> & { control?: SessionView['control'] },
  context: SessionControlContext,
): SessionControlAvailability {
  if (context.demoMode) {
    return {
      available: false,
      reasonCode: 'demo',
      reason: '演示模式不发送控制命令',
      actions: [],
    };
  }
  if (!context.paired) {
    return { available: false, reasonCode: 'unpaired', reason: '尚未完成配对', actions: [] };
  }
  if (!context.online) {
    return { available: false, reasonCode: 'offline', reason: '网络离线', actions: [] };
  }
  // v0.4.0-dev.26: Codex write adapter joined DSH; other sources stay
  // read-only until their own adapter is explicitly enabled.
  if (session.source !== 'dsh' && session.source !== 'codex') {
    return {
      available: false,
      reasonCode: 'non_dsh',
      reason: '当前仅支持 DSH / Codex 会话控制（其他来源只读）',
      actions: [],
    };
  }
  if (!context.relayReady) {
    return {
      available: false,
      reasonCode: 'relay_not_ready',
      reason: 'live relay 未就绪（需 HTTPS/配置/连接）',
      actions: [],
    };
  }
  if (!session.control) {
    return {
      available: false,
      reasonCode: 'default_off',
      reason: SESSION_CONTROL_DEFAULT_OFF_COPY,
      actions: [],
    };
  }
  if (!session.control.enabled) {
    // A server reason means the session was evaluated; the raw string is never
    // displayed. Allowlist/no-capability shapes become the fixed unauthorized
    // copy; anything else stays the default-off copy.
    if (isAllowlistDenialReason(session.control.reason)) {
      return {
        available: false,
        reasonCode: 'unauthorized',
        reason: SESSION_CONTROL_UNAUTHORIZED_COPY,
        actions: [],
      };
    }
    return {
      available: false,
      reasonCode: 'default_off',
      reason: SESSION_CONTROL_DEFAULT_OFF_COPY,
      actions: [],
    };
  }
  const actions = session.control.capabilities.filter(
    (capability): capability is SessionControlCapability =>
      SESSION_CONTROL_UI_ACTION_SET.has(capability),
  );
  if (actions.length === 0) {
    // Enabled but no supported capability granted for this session.
    return {
      available: false,
      reasonCode: 'unauthorized',
      reason: SESSION_CONTROL_UNAUTHORIZED_COPY,
      actions: [],
    };
  }
  return { available: true, actions };
}

/** True when the panel must not render send/interrupt controls at all. */
export function sessionControlHidesActions(availability: SessionControlAvailability): boolean {
  return availability.reasonCode === 'unauthorized';
}

export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

export type SessionCommandTextError = 'EMPTY_TEXT' | 'TEXT_TOO_LONG' | 'TEXT_NOT_ALLOWED';

export type ValidateCommandTextResult =
  { ok: true; text: string } | { ok: false; code: SessionCommandTextError; message: string };

/** Message commands: non-empty, ≤ 8 KiB UTF-8. Other actions must not carry text. */
export function validateCommandText(
  action: SessionCommandAction,
  rawText: string,
): ValidateCommandTextResult {
  if (action !== 'message') {
    if (rawText.trim().length > 0) {
      return {
        ok: false,
        code: 'TEXT_NOT_ALLOWED',
        message: '只有 message 命令可以携带文本',
      };
    }
    return { ok: true, text: '' };
  }
  const text = rawText;
  if (text.trim().length === 0) {
    return { ok: false, code: 'EMPTY_TEXT', message: '消息不能为空' };
  }
  const bytes = utf8ByteLength(text);
  if (bytes > SESSION_COMMAND_TEXT_MAX_BYTES) {
    return {
      ok: false,
      code: 'TEXT_TOO_LONG',
      message: `消息超出上限：${bytes} / ${SESSION_COMMAND_TEXT_MAX_BYTES} UTF-8 字节`,
    };
  }
  return { ok: true, text };
}

export type SessionCommandModeError = 'MODE_NOT_ALLOWED' | 'MODE_INVALID';

export type ValidateCommandModeResult =
  | { ok: true; mode?: SessionCommandMode }
  | { ok: false; code: SessionCommandModeError; message: string };

/**
 * Validate an optional per-command message mode before it is signed.
 *
 * `mode` is message-only (the protocol guard and daemon reject it on other
 * actions) and must be exactly `steer` or `queue`; anything else is rejected
 * client-side, so an invalid value can never be signed or sent.
 */
export function validateCommandMode(
  action: SessionCommandAction,
  mode: unknown,
): ValidateCommandModeResult {
  if (mode === undefined) return { ok: true };
  if (action !== 'message') {
    return {
      ok: false,
      code: 'MODE_NOT_ALLOWED',
      message: '只有 message 命令可以选择发送方式',
    };
  }
  if (!isSessionCommandMode(mode)) {
    return { ok: false, code: 'MODE_INVALID', message: '发送方式无效（仅支持 steer / queue）' };
  }
  return { ok: true, mode };
}

/** UUID v1–v5 idempotency key (uses crypto.randomUUID when available). */
export function newSessionCommandId(): string {
  const candidate = globalThis.crypto?.randomUUID?.();
  if (typeof candidate === 'string') return candidate;
  const bytes = randomBytes(16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
}

/** URL-safe single-use nonce for the signature transcript. */
export function newSessionCommandNonce(): string {
  return base64UrlEncode(randomBytes(24));
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export type SignedSessionCommand = SessionCommandPayload & {
  command_id: string;
  nonce: string;
  /** Phone-generated Unix seconds; part of the signed canonical transcript. */
  issued_at: number;
  signature: string;
};

export interface BuildSignedSessionCommandOptions {
  session: Pick<SessionView, 'source' | 'session_id'>;
  action: SessionCommandAction;
  text: string;
  /**
   * Optional per-command message mode. Included in the payload (and therefore
   * in the Ed25519 transcript) only when defined; omitted means “use the
   * daemon's configured default”. Invalid values / non-message placement are
   * rejected before any signing or sending happens.
   */
  mode?: SessionCommandMode;
  signingPrivateKey: string;
  commandId?: string;
  nonce?: string;
  /** Injectable clock for tests (defaults to Date.now). */
  now?: () => number;
}

export type BuildSignedSessionCommandResult =
  | { ok: true; payload: SignedSessionCommand; textHash: string; transcript: string }
  | {
      ok: false;
      code: SessionCommandTextError | SessionCommandModeError | 'INVALID_PAYLOAD';
      message: string;
    };

/**
 * Build the request payload and sign
 * `sessionCommandSigningPayload(payload, sha256Hex(text))` with the phone
 * Ed25519 key (standard-base64 signature, exactly as the daemon verifies it).
 *
 * `mode` (message only) must be set BEFORE the signing payload is built: the
 * protocol transcript appends `'\n' + mode` only when the field is defined, so a
 * steer and a queue command over identical text/ids have different signatures
 * and the mode cannot be swapped after signing.
 *
 * `issued_at` is generated by the phone and must be part of the canonical
 * transcript so a freshness window cannot be substituted with an unsigned
 * header/timestamp (M1.1 P2, task-37/38).
 */
export async function buildSignedSessionCommand(
  options: BuildSignedSessionCommandOptions,
): Promise<BuildSignedSessionCommandResult> {
  const validation = validateCommandText(options.action, options.text);
  if (!validation.ok) return validation;
  const modeValidation = validateCommandMode(options.action, options.mode);
  if (!modeValidation.ok) return modeValidation;
  const mode = modeValidation.mode;
  const commandId = options.commandId ?? newSessionCommandId();
  const nonce = options.nonce ?? newSessionCommandNonce();
  const nowMs = (options.now ?? Date.now)();
  const issuedAt = Math.floor(nowMs / 1000);
  if (!Number.isSafeInteger(issuedAt) || issuedAt <= 0) {
    return { ok: false, code: 'INVALID_PAYLOAD', message: '命令时间戳无效（issued_at）' };
  }
  const basePayload: SessionCommandPayload = {
    source: options.session.source,
    session_id: options.session.session_id,
    command: options.action,
    ...(options.action === 'message' ? { text: validation.text } : {}),
    ...(mode !== undefined ? { mode } : {}),
    command_id: commandId,
    nonce,
    issued_at: issuedAt,
  };
  const textHash = await sha256Hex(basePayload.text ?? '');
  const transcript = sessionCommandSigningPayload(basePayload, textHash);
  let signature: string;
  try {
    signature = await signStandardBase64(options.signingPrivateKey, transcript);
  } catch (error) {
    return {
      ok: false,
      code: 'INVALID_PAYLOAD',
      message: `命令签名失败：${error instanceof Error ? error.message : '未知错误'}`,
    };
  }
  const payload: SignedSessionCommand = {
    ...basePayload,
    command_id: commandId,
    nonce,
    issued_at: issuedAt,
    signature,
  };
  if (!isSignedSessionCommandPayload(payload)) {
    return { ok: false, code: 'INVALID_PAYLOAD', message: '命令 payload 未通过协议校验' };
  }
  return { ok: true, payload, textHash, transcript };
}

/**
 * Render a server-provided failure reason without trusting its shape. The
 * daemon promises already-redacted detail; the phone still strips control
 * characters, hides obvious secrets/paths, collapses whitespace and caps length.
 */
export function redactCommandDetail(detail: string | undefined): string | undefined {
  if (typeof detail !== 'string') return undefined;
  const singleLine = detail
    .replace(/[\u0000-\u001f\u007f]+/gu, ' ')
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/giu, 'Bearer [已隐藏]')
    .replace(/\bsk-[A-Za-z0-9_-]{8,}/gu, '[已隐藏的密钥]')
    .replace(/\b[A-Za-z]:\\[^\s]+/gu, '[已隐藏路径]')
    .replace(/(?:\/Users\/|\/home\/)[^\s]+/gu, '[已隐藏路径]')
    .replace(/\s+/gu, ' ')
    .trim();
  if (singleLine.length === 0) return undefined;
  return singleLine.length > 200 ? `${singleLine.slice(0, 200)}…` : singleLine;
}

/** Ownership conflict copy for `SESSION_OWNED` (DSH Desktop holds the session). */
export const SESSION_COMMAND_OWNERSHIP_COPY = '会话在 DSH Desktop 中打开，需先关闭再重试';

export const SESSION_COMMAND_FALLBACK_ERROR_COPY =
  '控制命令失败，请稍后重试或核对会话状态（不会自动重试）';

export const SESSION_COMMAND_TIMEOUT_COPY =
  '等待结果超时：未收到 session.command.result，不会自动重试（避免重复执行）';

/** Fixed, user-safe copy when the daemon result has no `detail`. */
export const SESSION_COMMAND_ERROR_MESSAGES: Record<string, string> = {
  CONFLICT: SESSION_COMMAND_OWNERSHIP_COPY,
  SESSION_OWNED: SESSION_COMMAND_OWNERSHIP_COPY,
  AUTH_REQUIRED: '需要重新验证设备，请解锁后重试',
  AUTH_INVALID: '设备授权无效，请重新配对或解锁',
  APPROVAL_REQUIRED: '该操作需要审批后才能执行',
  APPROVAL_EXPIRED: '审批已过期，请重新发起',
  APPROVAL_DENIED: '审批被拒绝，命令未执行',
  RISK_BLOCKED: '风险引擎阻止了该操作',
  RATE_LIMIT: '请求过于频繁，请稍后重试',
  EXEC_TIMEOUT: '执行超时，结果未知；请核对会话，不会自动重试',
  TOOL_NOT_FOUND: '目标工具不可用',
  NOT_FOUND: '目标会话不存在或已结束',
  INVALID_REQUEST: '请求无效（命令格式或会话状态不匹配）',
  INTERNAL: 'daemon 内部错误，请稍后重试',
};

/** True for conflict/ownership results (CONFLICT code or SESSION_OWNED detail). */
export function isSessionOwnershipConflict(input: { code?: unknown; detail?: unknown }): boolean {
  const code = typeof input.code === 'string' ? input.code.toUpperCase() : '';
  if (code === 'CONFLICT' || code === 'SESSION_OWNED') return true;
  const detail = typeof input.detail === 'string' ? input.detail : '';
  return /SESSION_OWNED|DSH\s*Desktop|already\s+open|owned\s+by/iu.test(detail);
}

/**
 * Pick the display message for a failure result: ownership copy first, then the
 * redacted server detail, then a fixed code message, then a generic fallback.
 */
export function sessionCommandErrorCopy(input: { code?: unknown; detail?: unknown }): string {
  if (isSessionOwnershipConflict(input)) return SESSION_COMMAND_OWNERSHIP_COPY;
  const detail = redactCommandDetail(typeof input.detail === 'string' ? input.detail : undefined);
  if (detail) return detail;
  const code = typeof input.code === 'string' ? input.code.toUpperCase() : '';
  return SESSION_COMMAND_ERROR_MESSAGES[code] ?? SESSION_COMMAND_FALLBACK_ERROR_COPY;
}

export type SessionCommandUiState =
  | 'draft'
  | 'confirming'
  | 'signing'
  | 'signed'
  | 'sent'
  | 'accepted'
  | 'succeeded'
  | 'failed'
  | 'timeout'
  | 'rejected'
  | 'unknown'
  | 'error';

export interface SessionCommandView {
  command_id: string;
  state: SessionCommandUiState;
  code?: string;
  detail?: string;
  audit_id?: string;
  /** Unix seconds (from the result event). */
  updated_at: number;
}

export function commandStateFromStatus(status: SessionCommandStatus): SessionCommandUiState {
  switch (status) {
    case 'accepted':
      return 'accepted';
    case 'succeeded':
      return 'succeeded';
    case 'failed':
      return 'failed';
    case 'timeout':
      return 'timeout';
    case 'rejected':
      return 'rejected';
    default:
      return 'unknown';
  }
}

const TERMINAL_STATUSES: ReadonlySet<SessionCommandStatus> = new Set([
  'succeeded',
  'failed',
  'timeout',
  'rejected',
  'unknown',
]);

export function isTerminalCommandStatus(status: SessionCommandStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Merge a result event into the view: idempotent (replays ignored), terminal
 * states never downgrade, and an `unknown` outcome may be reconciled later by a
 * definite succeeded/failed result. `accepted` is non-terminal.
 */
export function mergeCommandResult(
  current: SessionCommandView | undefined,
  result: SessionCommandResult,
  now = Math.floor(Date.now() / 1000),
): { changed: boolean; view: SessionCommandView } {
  const nextState = commandStateFromStatus(result.status);
  const base: SessionCommandView = {
    command_id: result.command_id,
    state: nextState,
    updated_at: result.ts,
    ...(result.code !== undefined ? { code: result.code } : {}),
    ...(redactCommandDetail(result.detail) !== undefined
      ? { detail: redactCommandDetail(result.detail) }
      : {}),
    ...(result.audit_id !== undefined ? { audit_id: result.audit_id } : {}),
  };
  if (!current) return { changed: true, view: base };
  const currentTerminal = TERMINAL_STATUSES.has(statusForState(current.state));
  const nextTerminal = isTerminalCommandStatus(result.status);
  if (currentTerminal) {
    // Reconcile only an explicitly unknown outcome; otherwise ignore replays.
    if (!nextTerminal) return { changed: false, view: current };
    if (current.state === 'unknown' && nextState !== 'unknown') {
      return { changed: true, view: base };
    }
    return { changed: false, view: current };
  }
  if (current.state === nextState && current.code === base.code && current.detail === base.detail) {
    return { changed: false, view: current };
  }
  // Local 'sent'/'signing' states are replaced by the server status.
  return { changed: true, view: { ...base, updated_at: result.ts || now } };
}

/** Map a UI state back to a protocol status for merge bookkeeping. */
function statusForState(state: SessionCommandUiState): SessionCommandStatus {
  if (state === 'accepted') return 'accepted';
  if (state === 'succeeded') return 'succeeded';
  if (state === 'failed') return 'failed';
  if (state === 'timeout') return 'timeout';
  if (state === 'rejected') return 'rejected';
  if (state === 'unknown') return 'unknown';
  return 'accepted';
}

export function applySessionCommandResult(
  commands: Record<string, SessionCommandView>,
  result: SessionCommandResult,
): Record<string, SessionCommandView> {
  const merged = mergeCommandResult(commands[result.command_id], result);
  if (!merged.changed) return commands;
  return { ...commands, [result.command_id]: merged.view };
}

/** Parse the generic `event` payload carrying a `session.command.result`. */
export function parseSessionCommandResultEvent(payload: unknown): SessionCommandResult | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const candidate = payload as Record<string, unknown>;
  if (candidate['event'] !== SESSION_COMMAND_RESULT_EVENT) return null;
  const data = candidate['data'];
  return isSessionCommandResult(data) ? data : null;
}

export interface SessionCommandInput {
  session: SessionView;
  action: SessionCommandAction;
  text: string;
  /**
   * Optional message admission mode. The panel only sets this when the daemon
   * advertises both modes; otherwise it stays undefined and the daemon default
   * applies (pre-mode signature transcript preserved).
   */
  mode?: SessionCommandMode;
}

export interface SessionCommandSendResult {
  ok: boolean;
  command_id?: string;
  error?: string;
}

export const SESSION_COMMAND_STATE_LABELS: Record<SessionCommandUiState, string> = {
  draft: '草稿',
  confirming: '待确认',
  signing: '签名中',
  signed: '已签名',
  sent: '已发送',
  accepted: '已受理（排队中）',
  succeeded: '已执行成功',
  failed: '执行失败',
  timeout: '结果超时',
  rejected: '已拒绝',
  unknown: '结果未知（需核对）',
  error: '发送失败',
};

export type SessionCommandTone = 'success' | 'failure' | 'pending' | 'neutral';

export interface SessionCommandPresentation {
  state: SessionCommandUiState;
  label: string;
  tone: SessionCommandTone;
  /** Readable (and redacted) outcome message; undefined for pending states. */
  message?: string;
  code?: string;
  /** True when the failure is the DSH Desktop ownership conflict. */
  ownershipConflict: boolean;
}

const FAILURE_STATES: ReadonlySet<SessionCommandUiState> = new Set([
  'failed',
  'rejected',
  'timeout',
  'unknown',
  'error',
]);

const PENDING_STATES: ReadonlySet<SessionCommandUiState> = new Set([
  'accepted',
  'sent',
  'signed',
  'signing',
]);

/**
 * Single source of truth for the result card: label, tone and readable message.
 * `rejected`/`failed`/`timeout`/`unknown` are always failures (never rendered as
 * success), ownership conflicts show the DSH Desktop copy, and missing detail
 * falls back to a fixed code message. A client-side timeout is shown when the
 * daemon has not answered in time (no automatic retry).
 */
export function sessionCommandPresentation(
  view: SessionCommandView,
  options: { timedOut?: boolean } = {},
): SessionCommandPresentation {
  const pending = PENDING_STATES.has(view.state);
  const state: SessionCommandUiState =
    options.timedOut === true && pending ? 'timeout' : view.state;
  const tone: SessionCommandTone =
    state === 'succeeded'
      ? 'success'
      : FAILURE_STATES.has(state)
        ? 'failure'
        : PENDING_STATES.has(state)
          ? 'pending'
          : 'neutral';
  const ownershipConflict =
    tone === 'failure' && isSessionOwnershipConflict({ code: view.code, detail: view.detail });
  const message =
    tone === 'failure'
      ? state === 'timeout' && view.detail === undefined
        ? SESSION_COMMAND_TIMEOUT_COPY
        : sessionCommandErrorCopy({ code: view.code, detail: view.detail })
      : state === 'accepted'
        ? '已受理，等待 daemon 执行；可稍后刷新查看结果'
        : undefined;
  return {
    state,
    label: SESSION_COMMAND_STATE_LABELS[state],
    tone,
    ...(message !== undefined ? { message } : {}),
    ...(view.code !== undefined ? { code: view.code } : {}),
    ownershipConflict,
  };
}
